// @ts-nocheck
import {
  Avatar,
  Box,
  Button,
  createFilterOptions,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomChip } from "../Custom/CustomIconButton";
import PropTypes from "prop-types";
import { CustomSelectInput } from "../Custom/Input";
import { useCallback, useMemo, useState } from "react";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import { Add, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { AddEmailModal } from "./AddEmailModal";

const filter = createFilterOptions({
  matchFrom: "any",
  stringify: (option) =>
    `${option.name} ${option.email} ${option?.searchName?.join(" ")}`,
});

export default function ClientEmailInput({
  values = [],
  setValues,
  options = [],
  readOnly = false,
  afterSuccessCb = () => {},
  errorMsg = false,
  disableAdd = false,
  showEmail = true,
  hideEmailFromTag = false,
  disableError = false,
  handleBlur = () => {},
  name = "emails",
  placeholder,
  onDeleteOption,
}) {
  const initialState = useMemo(() => {
    return {
      status: false,
    };
  }, []);
  // values and setValeus are like
  // it'll have sturcture of
  // _id: for primary and secondary client
  // name: for all users, including additional emails
  // email: for all users, including additional emails
  // [{_id, name, email}, {_id, name, email}];
  // on the values i'll be storing the whole objects
  const [email, setEmail] = useState("");
  const [addNewEmail, setAddNewEmail] = useState(initialState);

  const handleDelete = (optionToDelete) => {
    if (readOnly) return;
    setValues(values.filter((item) => item.email !== optionToDelete.email));
  };

  const handleEmailSaved = (data) => {
    const emailToEdit = addNewEmail?.emailToEdit;
    if (!emailToEdit) {
      afterSuccessCb(data);
      return;
    }

    setValues((currentValues) =>
      currentValues.map((item) =>
        item?.email === emailToEdit?.email
          ? { ...item, ...data, isExtraContact: true }
          : item,
      ),
    );
  };

  return (
    <>
      <CustomSelectInput
        sx={{
          "& input": {
            color: "white",
          },
        }}
        style={{
          marginBottom: name == "emails" ? "1rem" : "0rem",
          height: "auto",
        }}
        onChange={(_, value) => {
          setValues(value);
          setEmail("");
        }}
        fullWidth
        freeSolo={false}
        readOnly={readOnly}
        value={values}
        inputValue={email}
        isOptionEqualToValue={(option, value) => option.email === value.email}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option.name || option.email || "";
        }}
        filterOptions={filter}
        options={options}
        componentsProps={{
          paper: {
            style: {
              display: "block",
              background: "black",
              outline: "1px solid white",
              borderRadius: "8px",
            },
          },
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            if (!option || !option.email) return null;

            const firstLetter = (option.name || option.email || "?")
              .charAt(0)
              .toUpperCase();

            // MUI's getTagProps includes `key` — spreading it into JSX trips
            // React's "key via spread" warning. Pull it out and pass key directly.
            const { key: tagKey, ...tagProps } = getTagProps({ index });
            return (
              <CustomChip
                key={option.email ?? tagKey}
                avatar={
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: "0.8rem",
                    }}
                  >
                    {firstLetter}
                  </Avatar>
                }
                label={
                  hideEmailFromTag
                    ? option.name
                    : `${option.name} <${option.email}>`
                }
                {...tagProps}
                onDelete={() => handleDelete(option)}
                disabled={readOnly}
              />
            );
          })
        }
        renderOption={(props, option) => {
          const firstLetter = (option.name || option.email || "?")
            .charAt(0)
            .toUpperCase();
          return (
            <>
              {/* renderOption props also carry `key` — strip it before spreading */}
              <Box
                component="li"
                {...(({ key: _k, ...rest }) => rest)(props)}
                key={option.email}
                sx={{ display: "flex", width: "100%" }}
              >
                <Avatar
                  sx={{ width: 32, height: 32, mr: 1.5, fontSize: "1rem" }}
                >
                  {firstLetter}
                </Avatar>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: getCssVariableHandler({
                        variableName: "--white-rgb",
                      }),
                      opacity: "0.8",
                    }}
                  >
                    {showEmail ? option.email : option?.mobile}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "auto",
                  }}
                >
                  {(option?.isEventOwner || option?.isDefault) && (
                    <Typography
                      component="span"
                      sx={{
                        padding: "2px 10px",
                        border: "1px solid #D175B6",
                        borderRadius: "999px",
                        color: "#D175B6",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {option?.isEventOwner ? "Owner" : "Default"}
                    </Typography>
                  )}
                  {option?.isExtraContact && !option?.isEventOwner && (
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Edit extra email">
                        <IconButton
                          aria-label={`Edit ${option.email}`}
                          size="small"
                          onMouseDown={(event) => {
                            // Keep the option mounted until the click handler opens
                            // the edit modal. Otherwise the input blur closes the list
                            // before the click event reaches this button.
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setAddNewEmail({
                              status: true,
                              emailToEdit: option,
                            });
                          }}
                          sx={{ color: "#D175B6" }}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete extra email">
                        <IconButton
                          aria-label={`Delete ${option.email}`}
                          size="small"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onDeleteOption?.(option);
                          }}
                          sx={{ color: "#fff" }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
              {options?.at(-1)?.email == option?.email && !disableAdd && (
                <Button
                  component="li"
                  key={"Add New"}
                  onClick={() => {
                    setAddNewEmail((prev) => ({ ...prev, status: true }));
                  }}
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: " center",
                    cursor: "pointer",
                    padding: "6px 1rem",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                    },
                  }}
                >
                  <Avatar
                    sx={{ width: 32, height: 32, mr: 1.5, fontSize: "1rem" }}
                  >
                    <Add />
                  </Avatar>
                  <Box>
                    <Typography variant="body1">Add New Email</Typography>
                  </Box>
                </Button>
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              variant="outlined"
              name={name}
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                // Chrome ignores autocomplete="off" for some address fields.
                // This value prevents its saved-address popup from covering the
                // application's recipient list.
                autoComplete: "new-password",
              }}
              placeholder={placeholder ?? "Search or select emails"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleBlur}
            />

            {values?.length == 0 && !disableError && (
              <Typography color="red">
                {errorMsg ?? "At least one email should be here"}
              </Typography>
            )}
          </>
        )}
      />

      <AddEmailModal
        open={addNewEmail?.status}
        handleClose={() => {
          setAddNewEmail(initialState);
        }}
        afterSuccessCb={handleEmailSaved}
        emailToEdit={addNewEmail?.emailToEdit}
      />
    </>
  );
}

export function EmailInputForUser({
  values = [],
  setValues,
  options = [],
  readOnly = false,
  afterSuccessCb = () => {},
  errorMsg = false,
  disableAdd = false,
  showEmail = true,
  hideEmailFromTag = false,
  disableError = false,
  handleBlur = () => {},
  name = "emails",
  placeholder,
}) {
  const initialState = useMemo(() => {
    return {
      status: false,
    };
  }, []);
  // values and setValeus are like
  // it'll have sturcture of
  // _id: for primary and secondary client
  // name: for all users, including additional emails
  // email: for all users, including additional emails
  // [{_id, name, email}, {_id, name, email}];
  // on the values i'll be storing the whole objects
  const [email, setEmail] = useState("");
  const [addNewEmail, setAddNewEmail] = useState(initialState);

  const handleDelete = (optionToDelete) => {
    if (readOnly) return;
    setValues(values.filter((item) => item.email !== optionToDelete.email));
  };

  return (
    <>
      <CustomSelectInput
        sx={{
          "& input": {
            color: "white",
          },
        }}
        style={{
          marginBottom: name == "emails" ? "1rem" : "0rem",
          height: "auto",
        }}
        onChange={(_, value) => {
          setValues(value);
          setEmail("");
        }}
        fullWidth
        freeSolo={false}
        readOnly={readOnly}
        value={values}
        inputValue={email}
        isOptionEqualToValue={(option, value) => option.email === value.email}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option.name || option.email || "";
        }}
        filterOptions={filter}
        options={options}
        componentsProps={{
          paper: {
            style: {
              display: "block",
              background: "black",
              outline: "1px solid white",
              borderRadius: "8px",
            },
          },
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            if (!option || !option.email) return null;

            const firstLetter = (option.name || option.email || "?")
              .charAt(0)
              .toUpperCase();

            // MUI's getTagProps includes `key` — spreading it into JSX trips
            // React's "key via spread" warning. Pull it out and pass key directly.
            const { key: tagKey, ...tagProps } = getTagProps({ index });
            return (
              <CustomChip
                key={option.email ?? tagKey}
                avatar={
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: "0.8rem",
                    }}
                  >
                    {firstLetter}
                  </Avatar>
                }
                label={
                  hideEmailFromTag
                    ? option.name
                    : `${option.name} <${option.email}>`
                }
                {...tagProps}
                onDelete={() => handleDelete(option)}
                disabled={readOnly}
              />
            );
          })
        }
        renderOption={(props, option) => {
          const firstLetter = (option.name || option.email || "?")
            .charAt(0)
            .toUpperCase();
          return (
            <>
              {/* renderOption props also carry `key` — strip it before spreading */}
              <Box
                component="li"
                {...(({ key: _k, ...rest }) => rest)(props)}
                key={option.email}
              >
                <Avatar
                  sx={{ width: 32, height: 32, mr: 1.5, fontSize: "1rem" }}
                >
                  {firstLetter}
                </Avatar>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: getCssVariableHandler({
                        variableName: "--white-rgb",
                      }),
                      opacity: "0.8",
                    }}
                  >
                    {showEmail ? option.email : option?.mobile}
                  </Typography>
                </Box>
              </Box>
              {options?.at(-1)?.email == option?.email && !disableAdd && (
                <Button
                  component="li"
                  key={"Add New"}
                  onClick={() => {
                    setAddNewEmail((prev) => ({ ...prev, status: true }));
                  }}
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: " center",
                    cursor: "pointer",
                    padding: "6px 1rem",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                    },
                  }}
                >
                  <Avatar
                    sx={{ width: 32, height: 32, mr: 1.5, fontSize: "1rem" }}
                  >
                    <Add />
                  </Avatar>
                  <Box>
                    <Typography variant="body1">Add New Email</Typography>
                  </Box>
                </Button>
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              variant="outlined"
              name={name}
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                autoComplete: "new-password",
              }}
              placeholder={placeholder ?? "Search or select emails"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleBlur}
            />

            {values?.length == 0 && !disableError && (
              <Typography color="red">
                {errorMsg ?? "At least one email should be here"}
              </Typography>
            )}
          </>
        )}
      />

      <AddEmailModal
        open={addNewEmail?.status}
        handleClose={() => {
          setAddNewEmail(initialState);
        }}
        afterSuccessCb={afterSuccessCb}
      />
    </>
  );
}

const propType = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ),
  setValues: PropTypes.func,
  readOnly: PropTypes.bool,
  disableError: PropTypes.bool,
  afterSuccessCb: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ),
  errorMsg: PropTypes.string,
  disableAdd: PropTypes.bool,
  showEmail: PropTypes.bool,
  hideEmailFromTag: PropTypes.bool,
  handleBlur: PropTypes.func,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onDeleteOption: PropTypes.func,
};

ClientEmailInput.propTypes = propType;
EmailInputForUser.propTypes = propType;
